import {
  registryFlag,
  registryList,
  entriesForType,
  CONTENT,
  LAYOUT,
} from '../registry';

/** What a text field reads of the entry, which the two text-collecting types share. */
const TEXT_SETTINGS = [
  {
    name: 'upperCase',
    label: 'UPPER_CASE',
    control: 'switch',
    category: CONTENT,
  },
  {
    name: 'maxLength',
    label: 'MAX_LENGTH',
    control: 'number',
    min: 1,
    category: CONTENT,
  },
];

/** What a date field reads of the entry: how the date it collects is written. */
const DATE_SETTINGS = [
  {
    name: 'format',
    label: 'DATE_FORMAT',
    control: 'text',
    placeholder: 'dd/MM/yyyy',
    category: LAYOUT,
  },
];

/**
 * The field types a filter layout may choose from, and the catalogue data types each suits.
 *
 * Read by the layout editor to offer the sensible types for a key; `types` empty means the type fits
 * any key. `usesOptions` marks a type that reads a declared value map, `usesLookup` one that fetches
 * its values through a data source instead.
 *
 * `settings` is what the field reads of the entry beyond its key and value, in the vocabulary of
 * `engine/settings.js`; the editor offers exactly those.
 */
export const FILTER_TYPES = [
  {
    type: 'text',
    label: 'FILTER_TEXT',
    types: ['text', 'number'],
    settings: TEXT_SETTINGS,
  },
  {
    type: 'select',
    label: 'FILTER_SELECT',
    types: ['text', 'number', 'flag'],
    usesOptions: true,
    usesLookup: true,
    settings: [
      // What the procedure calls the text being typed, for a list asked again as it is.
      {
        name: 'searchParameter',
        label: 'SEARCH_PARAMETER',
        control: 'text',
        placeholder: 'Search',
        category: CONTENT,
        when: (entry) => Boolean(entry?.searchable),
      },
    ],
  },
  { type: 'number', label: 'FILTER_NUMBER', types: ['number'] },
  {
    type: 'numberRange',
    label: 'FILTER_NUMBER_RANGE',
    types: ['number'],
    isRange: true,
    usesScale: true,
  },
  {
    type: 'numberSet',
    label: 'FILTER_NUMBER_SET',
    types: ['number'],
  },
  {
    type: 'date',
    label: 'FILTER_DATE',
    types: ['date'],
    settings: DATE_SETTINGS,
  },
  { type: 'month', label: 'FILTER_MONTH', types: ['date'] },
  {
    type: 'dateRange',
    label: 'FILTER_DATE_RANGE',
    types: ['date'],
    isRange: true,
    settings: DATE_SETTINGS,
  },
  { type: 'boolean', label: 'FILTER_FLAG', types: ['flag'] },
  {
    type: 'segmented',
    label: 'FILTER_SEGMENTED',
    types: ['flag', 'number', 'text'],
    usesOptions: true,
    optionFields: ['label', 'icon', 'color'],
  },
];

/**
 * What a filter type reads of each option beyond its value, so the editor offers only those. A type
 * that does not say reads the wording alone.
 *
 * @param {String} type - A filter field type.
 *
 * @returns {Array}
 */
export const filterOptionFields = (type) => {
  const declared = registryList(FILTER_TYPES, 'type', type, 'optionFields');

  return declared.length > 0 ? declared : ['label'];
};

/**
 * What a filter type reads of the entry beyond its key, as the settings the editor offers for it.
 *
 * @param {String} type - A filter field type.
 *
 * @returns {Array}
 */
export const filterSettings = (type) =>
  registryList(FILTER_TYPES, 'type', type, 'settings');

/**
 * Whether a filter type is drawn on a scale, so the editor asks for its ends, step and unit.
 *
 * @param {String} type - A filter field type.
 *
 * @returns {Boolean}
 */
export const filterUsesScale = (type) =>
  registryFlag(FILTER_TYPES, 'type', type, 'usesScale');

/**
 * Whether a filter type collects two ends of a span, so its value is applied as a pair of terms
 * rather than one.
 *
 * @param {String} type - A filter field type.
 *
 * @returns {Boolean}
 */
export const filterIsRange = (type) =>
  registryFlag(FILTER_TYPES, 'type', type, 'isRange');

/**
 * Whether a filter type reads a declared value map, so the editor offers one for it.
 *
 * @param {String} type - A filter field type.
 *
 * @returns {Boolean}
 */
export const filterUsesOptions = (type) =>
  registryFlag(FILTER_TYPES, 'type', type, 'usesOptions');

/**
 * Whether a filter type fetches its values through a data source, so the editor asks for its key.
 *
 * @param {String} type - A filter field type.
 *
 * @returns {Boolean}
 */
export const filterUsesLookup = (type) =>
  registryFlag(FILTER_TYPES, 'type', type, 'usesLookup');

/**
 * The filter types worth offering for a catalogue type, most specific first.
 *
 * @param {String} type - A catalogue entry's data type.
 *
 * @returns {Array}
 */
export const filtersForType = (type) => entriesForType(FILTER_TYPES, type);
